/*
    *  Dialog view for allowing a user to create a new Fixture Device Group (factory_devicegroup) or
    *  edit the settings of an existing one.
    *
    *  The dialog allows the user to input a name for the to-be-created Fixture Device Group, as well
    *  as select the Target Production Device Group, the Production Device Group the blessed device will be 
    *  associated to (production_devicegroup).
*/

import Radio from 'backbone.radio';
import Marionette from 'backbone.marionette';
import _ from 'underscore';
import Handlebars from 'handlebars';
import DialogInterface from '@/interfaces/dialog';
import DeviceGroupList from '@/views/modules/selectable_device_group_list';
import strings_common from 'amdi18n-loader!@/nls/common';
import strings_specific from 'amdi18n-loader!@/nls/dialogs';
import template from '@/../templates/factory-devicegroup-settings.hbs';
var strings = _.extend({}, strings_common, strings_specific);

var MAX_NAME_LENGTH = 100;
var MAX_DESCRIPTION_LENGTH = 300;
var temp_production_target = { id: null };
var temp_dut_target = { id: null };
var messages = {
    save: {
        success: 'Successfully saved: '
    },
    create: {
        success: 'Successfully created: '
    }
};

var globalCh = Radio.channel('global');

function perm_check(active, primary, type){
    if ( type === 'factoryfixture_devicegroup' && active.permits(primary, 'update_factory_vars') ) {
        return true;
    } else if ( type === 'pre_factoryfixture_devicegroup' && active.permits(primary, 'update_pre_factory_vars') ){
        return true;
    }
    return false;
}


function get_readable_type(type) {
    var readable_type = 'Human Readable Device Group Type';
    switch (type) {
        case 'pre_factoryfixture_devicegroup':
            readable_type = 'Test Fixture Device Group';
            break;
        case 'factoryfixture_devicegroup':
            readable_type = 'Fixture Device Group';
            break;
        case 'pre_production_devicegroup':
            readable_type = 'Test Production Target';
            break;
        case 'production_devicegroup':
            readable_type = 'Production Target';
            break;
        case 'pre_dut_devicegroup':
            readable_type = 'Test DUT Target';
            break;
        case 'dut_devicegroup':
            readable_type = 'DUT Target';
            break;
        default:
            readable_type = 'Development Device Group';
    }
    return readable_type;
}

var View = Marionette.View.extend(DialogInterface).extend({
    template: Handlebars.compile(template),
    ui: {
        name: '.create-name',
        submit_btn: '.submit',
        load_spinner: '.loader',
        tooltips: '[data-toggle="tooltip"]',
        name_length_counter: '.name-counter',
        description: '.form-control.description',
        description_length_counter: '.description-counter',
        env_vars: '.environment-variables',
        env_vars_textarea: '.environment-variables textarea'
    },
    regions: {
        deployment_settings: '.deployment-settings',
        dut_devicegroup_list: '.dut-devicegroup-list',
        dut_pagination_controls: '.dut-pagination',
        production_devicegroup_list: '.production-devicegroup-list',
        production_pagination_controls: '.production-pagination',
    },
    events: {
        'submit form': 'handle_submit',
        'keyup @ui.name': 'validate',
        'paste @ui.name': 'validate',
        'change @ui.name': 'validate',
        'mouseup @ui.name': 'validate',
        'keyup @ui.description': 'validate',
        'paste @ui.description': 'validate',
        'change @ui.description': 'validate',
        'mouseup @ui.description': 'validate'
    },
    childViewEvents: {
        'production:target:choose:devicegroup': 'choose_production_target',
        'dut:target:choose:devicegroup': 'choose_dut_target',
        'production:page': function (filter) {
            let list_view = this.getRegion('production_devicegroup_list').currentView;
            list_view.setFilter(filter);
        },
        'dut:page': function (filter) {
            let list_view = this.getRegion('dut_devicegroup_list').currentView;
            list_view.setFilter(filter);
        }
    },
    initialize: function (options) {
        if ( this.getOption('devicegroup') ){
            this.model = this.getOption('devicegroup');
            this.owner = this.model.rel.get('owner');
            temp_production_target = this.model.rel.get('production_target');
            temp_dut_target = this.model.rel.get('dut_target');
        } else {
            temp_production_target = { id: null };
            temp_dut_target = { id: null };
            this.owner = Radio.request('global', 'active_account');
        }
        this.destroyOnClose();
        this.focusFirstInput();
    },
    templateContext: function(){
        let self = this;

        function get_production_target_type(){
            var target = 'production_devicegroup';
            if ( self.getOption('production_target_type') ) target = self.getOption('production_target_type');
            return get_readable_type(target);
        }
        function get_production_devices_type(){
            var title = 'Production Devices';
            if ( self.getOption('target_type') === 'pre_production_devicegroup') title = 'Test Production Devices';
            return title;
        }
        function get_dut_target_type(){
            var target = 'dut_devicegroup';
            if ( self.getOption('dut_target_type') ) target = self.getOption('dut_target_type');
            return get_readable_type(target); 
        }
        function get_dut_devices_type(){
            var title = 'DUT Devices';
            if ( self.getOption('target_type') === 'pre_production_devicegroup') title = 'Test DUT Devices';
            return title;
        }

        let type = get_readable_type(this.getOption('type'));
        let production_target_type = get_production_target_type();
        let production_target_devices_type = get_production_devices_type();
        let dut_target_type = get_dut_target_type();
        let dut_target_devices_type = get_dut_devices_type();
        let env_vars = null;
        let env_vars_keys = undefined;
        let edit_env_vars = false;
        let show_env_vars_keys = false;

        let active = globalCh.request('active_account');
        let primary = globalCh.request('primary_account');

        if ( this.model && this.model.get('env_vars') ) { 
            env_vars = JSON.stringify(this.model.get('env_vars'), null, 4);
        }
        if ( perm_check(active, primary, this.getOption('type') ) ) {
            edit_env_vars = true;
        } else {
            show_env_vars_keys = true;
            if ( this.model && this.model.get('env_vars') ) {
                env_vars_keys = Object.keys(this.model.get('env_vars'));
            }
        }
        if ( !env_vars_keys ) env_vars_keys = "no variables";

        var data = {
            s: strings,
            is_create: !this.model,
            type,
            production_target_type,
            production_target_devices_type,
            dut_target_type,
            dut_target_devices_type,
            env_vars: env_vars,
            env_vars_keys: env_vars_keys,
            edit_env_vars: edit_env_vars,
            show_env_vars_keys: show_env_vars_keys
        };

        return data;
    },
    validate: function () {
        var has_name = false;
        var has_prod_target = false;
        var has_dut_target = false;
        if ( this.ui.name.val() ) {
            this.check_validation(
                this.ui.name,
                MAX_NAME_LENGTH,
                this.ui.name_length_counter,
                this.ui.submit_btn
            );
            has_name = true;
        } else {
            this.check_validation(
                this.ui.name,
                MAX_NAME_LENGTH,
                this.ui.name_length_counter,
                this.ui.submit_btn
            );
        }

        if ( this.ui.description.val() ) {
            this.check_validation(
                this.ui.description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.description_length_counter,
                this.ui.submit_btn
            );
        } else {
            this.check_validation(
                this.ui.description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.description_length_counter,
                this.ui.submit_btn
            );
        }

        if ( temp_production_target.id || (this.model && this.model.rel.get('production_target').id) ){
            has_prod_target = true;
        }

        if ( temp_dut_target.id || (this.model && this.model.rel.get('dut_target').id) ){
            has_dut_target = true;
        }

        if (has_name && has_prod_target && has_dut_target) {
            this.ui.submit_btn.prop('disabled', false);
        } else {
            this.ui.submit_btn.prop('disabled', true);
        }

    },
    onRender: function () {
        this.validate();
        this.ui.tooltips.tooltip();

        var regions = Radio.request('api', 'cloud_regions');
        var show_regions = regions && regions.length;

        var production_target_list_view = new DeviceGroupList({
            collection: this.getOption('devicegroups'),
            product: this.getOption('product') || this.getOption('devicegroup') && this.getOption('devicegroup').rel.get('product'),
            devicegroup: this.getOption('devicegroup') && this.getOption('devicegroup').rel.get('production_target'),
            columns: _.compact(['select', 'name', show_regions && 'region', 'deployment']),
            type: this.getOption('production_target_type'),
            childViewEventPrefix: 'production:target'
        });

        var dut_target_list_view = new DeviceGroupList({
            collection: this.getOption('dut_devicegroups'),
            product: this.getOption('product') || this.getOption('devicegroup').rel.get('product'),
            devicegroup: this.getOption('devicegroup') && this.getOption('devicegroup').rel.get('dut_target'),
            columns: _.compact(['select', 'name', show_regions && 'region', 'deployment']),
            type: this.getOption('dut_target_type'),
            childViewEventPrefix: 'dut:target'
        });

        this.showChildView('production_devicegroup_list', production_target_list_view);
        this.showChildView('production_pagination_controls', this.getOption('production_pagination_controls'));
        this.showChildView('dut_devicegroup_list', dut_target_list_view);
        this.showChildView('dut_pagination_controls', this.getOption('dut_pagination_controls'));
    },
    handle_submit: function(e) {
        e.preventDefault();
        /* 
            * When submitting, it could be either an save or create
            * new action.
            * 
            * If the form was originally passed a devicegroup, then,
            * the view was given a model and we assume this to be an edit.
            * 
            * If the form did not have a devicegroup, then, assume
            * this is a create action.
         */

        let description = this.ui.description.val().trim();
        let name = this.ui.name.val().trim();
        let product = this.options.product; 

        let env_vars_string = this.ui.env_vars_textarea.val();
        let env_vars = null;
        let error = {
            responseJSON : {
                errors: []
            }
        };

        try {
           env_vars = JSON.parse(env_vars_string);
        } catch ( e ) {
            if ( env_vars_string && env_vars_string.length > 0 ) {
                error.responseJSON.errors.push({
                    detail: {
                        message : e.toString()
                    }
                });
            }
        }

        if ( error.responseJSON.errors.length > 0 ) {
            this.handle_error(error);
            return;
        }

        let env_vars_keys = null;
        if (env_vars) {
                env_vars_keys = Object.keys(env_vars);
                env_vars_keys.forEach( (k) => {
                if ( typeof env_vars[k] !== 'string' && !isFinite(env_vars[k]) ) {
                        error.responseJSON.errors.push({
                                    detail: {
                                        message : 'Illegal value for ' + k + '. Values must be strings or finite numbers. '
                                    }
                                }
                        );
                } else if ( typeof env_vars[k] === 'boolean' ) {
                    error.responseJSON.errors.push({
                                detail: {
                                    message : 'Illegal value for ' + k + '. Values must be strings or finite numbers. '
                                }
                            }
                    ); 
                }
            });
        }


        if ( error.responseJSON.errors.length > 0 ) {
            this.handle_error(error);
            return;
        }

        if ( this.model ){
            this.save_factory_devicegroup(name, description, env_vars);    
        } else {
            this.create_factory_devicegroup(name, product, description, env_vars);
        }
    },

    save_factory_devicegroup: function(name, description, env_vars) {
        let active = globalCh.request('active_account');
        let primary = globalCh.request('primary_account');

        let attributes = {
            name,
            description,
        };

        if ( perm_check(active, primary, this.model.type) ) {
            attributes.env_vars = env_vars;
        }

        this.model.save({
            data: {
                type: this.model.type,
                id: this.model.id,
                attributes: attributes,
                relationships: {
                    production_target: {
                        id: temp_production_target.id,
                        type: temp_production_target.type
                    },
                    dut_target: {
                        id: temp_dut_target.id,
                        type: temp_dut_target.type
                    }
                }
            }
        },
        {
            patch: true,
            success: function(model, response){
                this.on_success(model, messages.save.success + '"' + model.get('name') + '"');
            }.bind(this),
            error: function (model, response) {
                this.handle_error(response);
            }.bind(this)
        });
    },

    create_factory_devicegroup: function (name, product, description, env_vars) {
        var devicegroups = this.options.devicegroups;
        let active = globalCh.request('active_account');
        let primary = globalCh.request('primary_account');

        let opts = {
            name: name,
            description: description,
            type: this.options.type
        };

        if ( perm_check(active, primary, opts.type) ) {
            opts.env_vars = env_vars;
        }

        devicegroups.create( opts,
        {
            product: product,
            production_target: temp_production_target,
            dut_target: temp_dut_target,
            wait: true,
            success: function(model, response){
                this.on_success(model, messages.create.success + '"' + model.get('name') + '"');
            }.bind(this),
            error: function (model, response) {
                this.handle_error(response);
            }.bind(this),
        });
    },

    choose_production_target: function (new_production_target) {
        if ( new_production_target ){
            temp_production_target = new_production_target;
        }
        this.validate();
    },

    choose_dut_target: function (new_dut_target) {
        if ( new_dut_target ){
            temp_dut_target = new_dut_target;
        }
        this.validate();
    },

    on_success: function (new_factory_devicegroup, message) {
        Radio.request('alerts', 'showAlert', {
            message: message,
            messageType: 'success'
        });
        this.closeDialog();
    },
    on_error: function (error) {
        this.showAlert('alert-danger', error);
    },
    handle_error: function(err){
        this.showAlert('failure', err);
        this.ui.load_spinner.hide();
        this.listenToOnce(this, 'input', function(){
            this.hideAlert();
        }.bind(this));
    }
});
export default View;

